import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const PINGBIX_FIELDS = [
  // WhatsApp
  { key: "PINGBIX_API_KEY", label: "WhatsApp API Key", group: "whatsapp", type: "password" },
  { key: "PINGBIX_SENDER", label: "WhatsApp Sender (e.g. +91…)", group: "whatsapp" },
  { key: "PINGBIX_WHATSAPP_OTP_CAMPAIGN_NAME", label: "Campaign Name", group: "whatsapp" },
  { key: "PINGBIX_WHATSAPP_OTP_TEMPLATE_NAME", label: "Template Name", group: "whatsapp" },
  // SMS
  { key: "PINGBIX_SMS_API_KEY", label: "SMS API Key", group: "sms", type: "password" },
  { key: "PINGBIX_USERID", label: "User ID", group: "sms" },
  { key: "PINGBIX_PASSWORD", label: "Password", group: "sms", type: "password" },
  { key: "PINGBIX_SENDER_ID", label: "DLT Sender ID", group: "sms" },
  { key: "PINGBIX_ENTITY_ID", label: "DLT Entity ID", group: "sms" },
  { key: "PINGBIX_TEMPLATE_ID", label: "DLT Template ID", group: "sms" },
];

/**
 * Per-tenant Pingbix override panel. Any field left empty falls back to the
 * platform's default Pingbix credentials. Bring-your-own-account use case.
 */
export default function PingbixSettings() {
  const [vals, setVals] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/admin/pingbix")
      .then(({ data }) => alive && setVals(data?.pingbix || {}))
      .catch(() => toast.error("Failed to load Pingbix settings"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/pingbix", vals);
      toast.success("Pingbix settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setVals((prev) => ({ ...prev, [k]: v }));

  const renderGroup = (group, title, hint) => (
    <div className="bg-white border border-[hsl(var(--kb-border))] p-6 space-y-4" data-testid={`pingbix-${group}`}>
      <div>
        <div className="overline">{title}</div>
        <p className="text-xs text-[hsl(var(--kb-muted))] mt-1">{hint}</p>
      </div>
      {PINGBIX_FIELDS.filter((f) => f.group === group).map((f) => (
        <div key={f.key}>
          <label className="overline block mb-1">{f.label}</label>
          <input
            type={f.type || "text"}
            value={vals[f.key] || ""}
            onChange={(e) => set(f.key, e.target.value)}
            placeholder="leave blank to use platform default"
            className="kb-input"
            data-testid={`pingbix-${f.key.toLowerCase()}`}
            autoComplete="off"
          />
        </div>
      ))}
    </div>
  );

  return (
    <div className="max-w-3xl space-y-5" data-testid="admin-pingbix-page">
      <div>
        <div className="overline mb-2">OTP Delivery</div>
        <h1 className="font-display text-4xl">Pingbix (SMS & WhatsApp)</h1>
        <p className="text-sm text-[hsl(var(--kb-muted))] mt-2 max-w-2xl">
          Bring your own Pingbix account or leave every field blank to use the
          platform's shared account (billed via your wallet). Customers see a
          choice of SMS or WhatsApp on the login screen — both channels need
          their respective fields configured here OR on the platform.
        </p>
      </div>
      {renderGroup(
        "whatsapp",
        "WhatsApp",
        "Pingbix WhatsApp Business API. Used when customer picks WhatsApp at login."
      )}
      {renderGroup(
        "sms",
        "SMS (DLT-compliant)",
        "Pingbix SMSApi. DLT template approval required from your operator panel."
      )}
      <button
        onClick={save}
        disabled={loading || saving}
        className="kb-btn-primary"
        data-testid="pingbix-save"
      >
        {saving ? "Saving…" : "Save Pingbix settings"}
      </button>
    </div>
  );
}
